import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Stack,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  DoneAll as MarkAllReadIcon,
  DeleteSweep as ClearAllIcon,
  NotificationsOff as EmptyIcon,
} from '@mui/icons-material';
import { useNotifications, useNotificationMutations } from '../../hooks/useNotifications';
import { NotificationItem } from './NotificationItem';

interface NotificationPanelProps {
  open: boolean;
  onClose: () => void;
}

export const NotificationPanel: React.FC<NotificationPanelProps> = ({
  open,
  onClose,
}) => {
  const { data, isLoading } = useNotifications({ limit: 50 });
  const mutations = useNotificationMutations();

  const notifications = data?.data?.notifications || [];
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const handleMarkAsRead = async (id: string) => {
    try {
      await mutations.markAsRead.mutateAsync(id);
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await mutations.markAllAsRead.mutateAsync();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await mutations.deleteNotification.mutateAsync(id);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleClearAll = async () => {
    try {
      await mutations.clearAllRead.mutateAsync();
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 400 },
          maxWidth: '100%',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          backgroundColor: 'background.paper',
          zIndex: 1,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={600}>
            Notifications
            {unreadCount > 0 && (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                ({unreadCount} unread)
              </Typography>
            )}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* Actions */}
        {notifications.length > 0 && (
          <Stack direction="row" spacing={1} mt={1.5}>
            <Button
              size="small"
              startIcon={<MarkAllReadIcon />}
              onClick={handleMarkAllAsRead}
              disabled={unreadCount === 0 || mutations.markAllAsRead.isPending}
            >
              Mark all read
            </Button>
            <Button
              size="small"
              startIcon={<ClearAllIcon />}
              onClick={handleClearAll}
              disabled={mutations.clearAllRead.isPending}
              color="inherit"
            >
              Clear read
            </Button>
          </Stack>
        )}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress />
          </Box>
        ) : notifications.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={8}
            px={4}
          >
            <EmptyIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No notifications
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              You're all caught up! New notifications will appear here.
            </Typography>
          </Box>
        ) : (
          <Box>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification._id}>
                <NotificationItem
                  notification={notification}
                  onMarkAsRead={handleMarkAsRead}
                  onDelete={handleDelete}
                  onClose={onClose}
                />
                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default NotificationPanel;
